import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';
import { StrokeIcons } from '../../components/icons/GrayIconBox';
import { getAnonymousUserId } from '../../utils/anonymousUser';
import { getApiUrl } from '../../utils/apiConfig';
import { getMetaMatchParams } from '../../utils/analytics';
import { downloadAndOpen } from '../../utils/downloadUtils';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import {
  describeEventGenerationError,
  isResumableGenerationCode,
  parseErrorDetail,
  streamEventSample,
  clearPendingSampleRequest,
  loadPendingSampleRequest,
  savePendingSampleRequest,
} from './eventSampleStream';
import {
  trackClassPlayEvent,
  trackFreeTrialStart,
} from '../../utils/eventTracker';
import { copyToClipboard, shareContent, shareToKakao } from '../../utils/shareUtils';
import { setPendingFreeTrial } from '../../utils/freeTrialPending';
import {
  buildChatHandoffUrl,
  createChatHandoffPayload,
  storeChatHandoff,
} from '../../utils/chatHandoff';
import {
  buildStartTrialMetaPayload,
  createStartTrialEventId,
  trackMetaStartTrialPixel,
} from '../../utils/paymentTracking';
import {
  buildClassPlayPrompt,
  CLASS_PLAY_CLASS_PRESETS,
  CLASS_PLAY_DEFAULT_CLASS_LABEL,
  CLASS_PLAY_EXAMPLES,
  CLASS_PLAY_GAME_TYPES,
  CLASS_PLAY_MAX_CLASS_LABEL_CHARS,
  CLASS_PLAY_MAX_FREE_TOPIC_CHARS,
  CLASS_PLAY_MAX_PHRASE_CHARS,
  CLASS_PLAY_MAX_STORY_CHARS,
  CLASS_PLAY_LOCKED_STYLE,
  CLASS_PLAY_QUESTION_COUNTS,
  CLASS_PLAY_TOPICS,
  getClassPlayGameType,
  getClassPlayQuestionCount,
  getClassPlayTopic,
  getDefaultClassPlayGameType,
  getDefaultClassPlayQuestionCount,
  getDefaultClassPlayTopic,
  normalizeClassPlayClassLabel,
  normalizeClassPlayFreeTopic,
  normalizeClassPlayPhrase,
  normalizeClassPlayStory,
} from '../../config/classPlayEventConfig';
import ClassPhotoTrialGate from '../../components/events/ClassPhotoTrialGate';
import EventSlideViewer from '../../components/events/EventSlideViewer';
import './ClassPhotoEventPage.css';
import './ClassPlayEventPage.css';

const RESULT_STORAGE_KEY = 'whatquiz_class_play_result';
const DRAFT_STORAGE_KEY = 'whatquiz_class_play_draft';
const PENDING_SAMPLE_STORAGE_KEY = 'whatquiz_class_play_pending_sample';
const ACTIVITY_DRAFT_KEY = 'whatquiz_class_activity_draft';
const SSE_INACTIVITY_TIMEOUT = 300_000;
const LONG_WAIT_MS = 180_000;
const LOADING_STEPS = ['구성', '표지', '문제', '마무리'];

const toFriendlyLoadingMessage = (raw, fallback) => {
  const text = String(raw || '').trim();
  if (!text || /백업|원본|upload|storage|ffmpeg|encode|편집용/i.test(text)) {
    return fallback;
  }
  return text;
};

const asCoverDataUrl = (value) => (
  String(value || '').startsWith('data:') ? value : ''
);

const httpsAssetUrls = (values) => (
  (Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter((value) => value.startsWith('https://'))
);

const canvasCoverFromImage = (image) => {
  if (!image || !image.naturalWidth || !image.naturalHeight) return '';
  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext('2d').drawImage(image, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return '';
  }
};

const toShareErrorMessage = (error, hasResult) => {
  const raw = String(error?.message || '').trim();
  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    return '공유 서버에 연결하지 못했어요. 잠시 후 다시 눌러주세요.';
  }
  if (hasResult && /먼저 .+만든 뒤/.test(raw)) {
    return '공유 링크를 아직 만들지 못했어요. 잠시 후 다시 눌러주세요.';
  }
  return raw || '공유 화면을 열지 못했어요.';
};

const createEventId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `class-play-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const GENERATION_STREAM_MESSAGES = {
  closed: '연결이 잠깐 끊겨 놀이 PPT를 받지 못했어요. 화면을 켜 둔 채로 다시 눌러주세요.',
  failed: '놀이 PPT를 만들지 못했어요.',
  timeout: '놀이 PPT를 만드는 중 서버 응답이 없어요. 다시 시도해주세요.',
};
const GENERATION_NETWORK_MESSAGE = '인터넷 연결이 불안정해요. 연결을 확인하고 다시 눌러주세요.';
const GENERATION_RESUME_MESSAGE = '연결이 잠깐 끊겼어요. 만들어 둔 놀이 PPT를 불러오고 있어요';
const GENERATION_RESUME_DEADLINE_MS = 8 * 60_000;

const ClassPlayEventPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscriptionStatus, fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  const isPro = Boolean(subscriptionStatus?.isPro);
  const isAnonymousAtMountRef = useRef(!currentUser);
  const sharePreparationRef = useRef(null);
  const shareRecordRef = useRef(null);
  const previewsRef = useRef([]);
  const coverDataUrlRef = useRef('');
  const prepareShareRecordRef = useRef(null);
  const attachShareAssetsRef = useRef(null);
  const downloadUrlRef = useRef('');
  const resultPagesRef = useRef([]);

  const [classLabel, setClassLabel] = useState(CLASS_PLAY_DEFAULT_CLASS_LABEL);
  const [customClass, setCustomClass] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [classStory, setClassStory] = useState('');
  const [gameTypeId, setGameTypeId] = useState(getDefaultClassPlayGameType().id);
  const [topicId, setTopicId] = useState(getDefaultClassPlayTopic().id);
  const [freeTopic, setFreeTopic] = useState('');
  const [questionCount, setQuestionCount] = useState(getDefaultClassPlayQuestionCount().count);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [resultPages, setResultPages] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [shareRecord, setShareRecord] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isSharedResult, setIsSharedResult] = useState(false);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [trialGateIntent, setTrialGateIntent] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const [exampleSlide, setExampleSlide] = useState(0);
  const [manualShareUrl, setManualShareUrl] = useState('');
  const [fromActivity, setFromActivity] = useState(false);
  const [showAccountActions, setShowAccountActions] = useState(false);
  const [accountActionBusy, setAccountActionBusy] = useState('');
  const [previewLightbox, setPreviewLightbox] = useState(null);
  const [coverReady, setCoverReady] = useState(false);

  const selectedGame = getClassPlayGameType(gameTypeId);
  const selectedStyle = CLASS_PLAY_LOCKED_STYLE;
  const selectedCount = getClassPlayQuestionCount(questionCount);
  const normalizedClassLabel = useMemo(
    () => normalizeClassPlayClassLabel(classLabel),
    [classLabel],
  );
  const canGenerate = Boolean(normalizedClassLabel && !isGenerating);
  const variantId = `${selectedGame.id}_${selectedStyle.id}`;
  const previewDone = previews.filter(Boolean).length;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('share_expired') === '1') {
      setNotice('공유된 놀이 PPT는 만료됐어요. 우리 반 이름으로 새로 만들어보세요.');
    }
    if (params.get('from') === 'class-activity') {
      setFromActivity(true);
      trackClassPlayEvent('cross_handoff_landed', {
        entry: 'class_activity',
        isAnonymous: isAnonymousAtMountRef.current,
        isPro: false,
        stage: 'event_entry',
        targetCategory: 'class_play',
      });
    }
    trackClassPlayEvent('landing_viewed', {
      campaign: 'class_play_2026_q4',
      entry: params.get('utm_source') === 'kakao'
        ? 'kakao_share'
        : (params.get('from') === 'class-activity' ? 'class_activity' : 'event_page'),
      isAnonymous: isAnonymousAtMountRef.current,
      isPro: false,
      stage: 'event_entry',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const shareId = new URLSearchParams(window.location.search).get('share');
    if (shareId) {
      fetch(getApiUrl(`/api/class-plays/share/${encodeURIComponent(shareId)}`))
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) throw new Error(data?.detail || '공유 결과를 열지 못했어요.');
          if (cancelled) return;
          const sharedPages = data.pages?.length
            ? data.pages
            : (data.image_url ? [data.image_url] : []);
          resultPagesRef.current = sharedPages;
          downloadUrlRef.current = data.download_url || '';
          setResultPages(sharedPages);
          setDownloadUrl(data.download_url || '');
          setClassLabel(data.class_label || CLASS_PLAY_DEFAULT_CLASS_LABEL);
          setCustomClass(Boolean(
            data.class_label && !CLASS_PLAY_CLASS_PRESETS.includes(data.class_label),
          ));
          setShareRecord({
            ...data,
            share_url: `${window.location.origin}/s/class-play/${shareId}`,
          });
          setIsSharedResult(true);
          trackClassPlayEvent('shared_result_viewed', {
            entry: 'kakao_share',
            isAnonymous: isAnonymousAtMountRef.current,
            isPro: false,
            shareId,
            stage: 'shared_result',
          });
        })
        .catch((shareError) => {
          if (!cancelled) setError(shareError.message);
        });
      return () => {
        cancelled = true;
      };
    }

    try {
      const saved = JSON.parse(sessionStorage.getItem(RESULT_STORAGE_KEY) || 'null');
      if (saved?.pages?.length) {
        resultPagesRef.current = saved.pages;
        downloadUrlRef.current = saved.downloadUrl || '';
        setResultPages(saved.pages);
        setDownloadUrl(saved.downloadUrl || '');
        setClassLabel(saved.classLabel || CLASS_PLAY_DEFAULT_CLASS_LABEL);
        setCustomClass(Boolean(
          saved.classLabel && !CLASS_PLAY_CLASS_PRESETS.includes(saved.classLabel),
        ));
        setGameTypeId(getClassPlayGameType(saved.gameType).id);
        setTopicId(getClassPlayTopic(saved.topic).id);
        setFreeTopic(saved.freeTopic || '');
        setQuestionCount(getClassPlayQuestionCount(saved.questionCount).count);
        setPhrase(saved.phrase || '');
        setClassStory(saved.classStory || '');
        coverDataUrlRef.current = asCoverDataUrl(saved.coverDataUrl);
        if (saved.shareRecord?.share_url && saved.shareRecord?.image_url) {
          shareRecordRef.current = saved.shareRecord;
          setShareRecord(saved.shareRecord);
          setCoverReady(true);
        } else if (coverDataUrlRef.current) {
          setCoverReady(true);
        }
      } else {
        const draft = JSON.parse(sessionStorage.getItem(DRAFT_STORAGE_KEY) || 'null');
        if (draft) {
          setClassLabel(draft.classLabel || CLASS_PLAY_DEFAULT_CLASS_LABEL);
          setCustomClass(Boolean(
            draft.classLabel && !CLASS_PLAY_CLASS_PRESETS.includes(draft.classLabel),
          ));
          setPhrase(draft.phrase || '');
          setClassStory(draft.classStory || '');
          setGameTypeId(getClassPlayGameType(draft.gameType).id);
          setTopicId(getClassPlayTopic(draft.topic).id);
          setFreeTopic(draft.freeTopic || '');
          setQuestionCount(getClassPlayQuestionCount(draft.questionCount).count);
          if (draft.from === 'class_activity') setFromActivity(true);
        }
      }
    } catch (_) {
      sessionStorage.removeItem(RESULT_STORAGE_KEY);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (resultPages.length || CLASS_PLAY_EXAMPLES.length < 2) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setExampleSlide((prev) => (prev + 1) % CLASS_PLAY_EXAMPLES.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [resultPages.length]);

  useEffect(() => {
    if (!isGenerating) return undefined;
    const messages = [
      '픽셀 놀이판을 그리고 있어요',
      '표지와 진행 방법을 깔고 있어요',
      '문제가 들어오는 대로 보여드려요',
      'TV에 바로 틀 수 있게 다듬고 있어요',
    ];
    let index = 0;
    setLoadingMessage(messages[index]);
    setLoadingStep(index);
    const stepTimer = window.setInterval(() => {
      index = Math.min(index + 1, messages.length - 1);
      setLoadingStep(index);
      setLoadingMessage((current) => (
        /\d+\/\d+/.test(current) ? current : messages[index]
      ));
    }, 9000);
    const waitTimer = window.setTimeout(() => {
      setLoadingMessage((current) => (
        /\d+\/\d+/.test(current) ? current : '조금만 기다려주세요, 문제를 만들고 있어요'
      ));
    }, LONG_WAIT_MS);
    return () => {
      window.clearInterval(stepTimer);
      window.clearTimeout(waitTimer);
    };
  }, [isGenerating]);

  useEffect(() => {
    if (!previewLightbox) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setPreviewLightbox(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewLightbox]);

  useEffect(() => {
    shareRecordRef.current = shareRecord;
  }, [shareRecord]);

  useEffect(() => {
    if (isSharedResult || !coverReady) return undefined;
    prepareShareRecordRef.current?.().catch(() => {});
    return undefined;
  }, [coverReady, isSharedResult]);

  const persistDraft = () => {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
      classLabel,
      phrase,
      classStory,
      gameType: gameTypeId,
      topic: topicId,
      freeTopic,
      questionCount,
      style: selectedStyle.id,
    }));
  };

  const handleGenerate = async () => {
    if (!normalizedClassLabel) {
      setError('반 이름을 적어주세요.');
      return;
    }
    setError('');
    setQuotaExhausted(false);
    setIsGenerating(true);
    setPreviews([]);
    previewsRef.current = [];
    coverDataUrlRef.current = '';
    sharePreparationRef.current = null;
    shareRecordRef.current = null;
    setShareRecord(null);
    setCoverReady(false);
    downloadUrlRef.current = '';
    resultPagesRef.current = [];
    setDownloadUrl('');
    setResultPages([]);
    setLoadingStep(0);
    setLoadingMessage('픽셀 놀이판을 그리고 있어요');
    const startedAt = Date.now();
    // 이미지 단계까지 갔다가 끊긴 요청이 있으면 구성 단계를 건너뛰고 같은 요청으로 결과를 이어받는다.
    const inputSignature = JSON.stringify({
      classLabel: normalizedClassLabel,
      phrase,
      classStory,
      gameType: gameTypeId,
      topic: topicId,
      freeTopic,
      questionCount,
      style: selectedStyle.id,
      user: currentUser?.uid || null,
    });
    const pendingImageRequest = loadPendingSampleRequest(PENDING_SAMPLE_STORAGE_KEY, inputSignature);
    const eventId = pendingImageRequest?.eventId || createEventId();
    const sampleRequestId = createEventId();
    try {
      persistDraft();
      const token = currentUser ? await currentUser.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const visitorId = getAnonymousUserId();

      trackClassPlayEvent('generation_started', {
        entry: 'event_page',
        eventId,
        isAnonymous: !currentUser,
        isPro: false,
        stage: 'visual_generation',
        variantId,
      });

      let imageRequestBody = pendingImageRequest?.body || null;
      if (!imageRequestBody) {
        const prompt = buildClassPlayPrompt({
          classLabel: normalizedClassLabel,
          gameType: gameTypeId,
          topic: topicId,
          freeTopic,
          phrase,
          classStory,
          questionCount,
        });
        const outlineResponse = await fetchWithRetry(
          getApiUrl('/api/ppt/generate-outline-from-session'),
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              conversation_messages: [{ role: 'user', content: prompt }],
              ppt_type: 'lesson',
              visual_richness: 'auto',
              event_visitor_id: visitorId,
              sample_required: true,
            }),
          },
          { maxRetries: 1, timeoutMs: 60000 },
        );
        if (!outlineResponse.ok) {
          throw await parseErrorDetail(outlineResponse);
        }
        const outlineData = await outlineResponse.json();
        if (!outlineData?.success || !outlineData?.outline) {
          throw new Error(outlineData?.message || '놀이 PPT 구성을 만들지 못했어요.');
        }

        setLoadingMessage(`슬라이드를 그리고 있어요 · 0/${selectedCount.slides}`);
        const outline = {
          ...outlineData.outline,
          theme: selectedStyle.theme,
        };
        imageRequestBody = JSON.stringify({
          outline,
          generation_mode: 'image',
          style_description: selectedStyle.styleDescription,
          sample_request_id: sampleRequestId,
          event_visitor_id: visitorId,
          sample_required: true,
          class_play_event_id: eventId,
          event_source_url: `${window.location.origin}${window.location.pathname}`,
          ...getMetaMatchParams(),
        });
      }
      savePendingSampleRequest(PENDING_SAMPLE_STORAGE_KEY, { body: imageRequestBody, eventId, inputSignature });

      const { complete, resumed } = await streamEventSample({
        url: getApiUrl('/api/ppt/generate-file'),
        init: { method: 'POST', headers, body: imageRequestBody },
        networkMessage: GENERATION_NETWORK_MESSAGE,
        inProgressCode: 'ppt_sample_in_progress',
        resumeDeadlineMs: GENERATION_RESUME_DEADLINE_MS,
        onResume: () => setLoadingMessage(GENERATION_RESUME_MESSAGE),
        readOptions: {
          timeoutMs: SSE_INACTIVITY_TIMEOUT,
          messages: GENERATION_STREAM_MESSAGES,
          onEvent: (eventData) => {
            if (eventData.type === 'progress') {
              setLoadingMessage(toFriendlyLoadingMessage(
                eventData.message,
                '픽셀 슬라이드를 그리고 있어요',
              ));
            }
            if (eventData.type === 'page_preview') {
              const idx = (eventData.page || 1) - 1;
              const total = eventData.total || (idx + 1);
              const next = (previewsRef.current || []).slice();
              while (next.length < total) next.push(null);
              next[idx] = eventData.thumbnail;
              previewsRef.current = next;
              setPreviews(next);
              if (idx === 0) {
                coverDataUrlRef.current = asCoverDataUrl(eventData.thumbnail);
                if (coverDataUrlRef.current) setCoverReady(true);
              }
              const done = next.filter(Boolean).length;
              setLoadingStep(Math.min(3, done >= total ? 3 : done + 1));
              setLoadingMessage(`${done}/${total} 슬라이드가 들어왔어요`);
            }
          },
        },
      });
      clearPendingSampleRequest(PENDING_SAMPLE_STORAGE_KEY);
      const uploadedPages = (complete.original_image_urls || []).filter(Boolean);
      const pages = uploadedPages.length ? uploadedPages : previewsRef.current.filter(Boolean);
      resultPagesRef.current = pages;
      downloadUrlRef.current = complete.download_url || '';
      setResultPages(pages);
      setDownloadUrl(complete.download_url || '');
      setIsSharedResult(false);
      attachShareAssets().catch(() => {});
      sessionStorage.setItem(RESULT_STORAGE_KEY, JSON.stringify({
        pages,
        downloadUrl: complete.download_url || '',
        coverDataUrl: coverDataUrlRef.current,
        shareRecord: shareRecordRef.current,
        style: selectedStyle.id,
        gameType: selectedGame.id,
        topic: topicId,
        freeTopic: normalizeClassPlayFreeTopic(freeTopic),
        questionCount: selectedCount.count,
        classLabel: normalizedClassLabel,
        phrase: normalizeClassPlayPhrase(phrase),
        classStory: normalizeClassPlayStory(classStory),
      }));
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      trackClassPlayEvent('generation_completed', {
        elapsedMs: Date.now() - startedAt,
        entry: 'event_page',
        eventId,
        isAnonymous: !currentUser,
        isPro: false,
        stage: resumed ? 'visual_ready_resumed' : 'visual_ready',
        variantId,
      });
    } catch (generationError) {
      const failure = describeEventGenerationError(generationError, {
        alreadyUsedCode: 'ppt_sample_already_used',
        inProgressCode: 'ppt_sample_in_progress',
        messages: {
          alreadyUsed: currentUser
            ? '로그인 무료 결과까지 만들었어요. 가입하면 7일 PRO로 계속 만들 수 있어요.'
            : '무료 놀이 PPT를 만들었어요. 가입하면 한 번 더 만들 수 있어요.',
          failed: '놀이 PPT를 만들지 못했어요.',
          inProgress: '앞에서 누른 놀이 PPT를 아직 만들고 있어요. 1~2분 뒤 다시 눌러주세요.',
          network: GENERATION_NETWORK_MESSAGE,
        },
      });
      if (!isResumableGenerationCode(failure.code, 'ppt_sample_in_progress')) {
        clearPendingSampleRequest(PENDING_SAMPLE_STORAGE_KEY);
      }
      setQuotaExhausted(failure.exhausted);
      setError(failure.message);
      trackClassPlayEvent(failure.action, {
        elapsedMs: Date.now() - startedAt,
        entry: 'event_page',
        errorCode: failure.code,
        eventId,
        isAnonymous: !currentUser,
        isPro: false,
        stage: 'visual_generation',
        variantId,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const prepareShareRecord = () => {
    if (shareRecordRef.current?.share_url && shareRecordRef.current?.image_url) {
      if (downloadUrlRef.current && !shareRecordRef.current.download_url) {
        return attachShareAssetsRef.current();
      }
      return Promise.resolve(shareRecordRef.current);
    }
    if (sharePreparationRef.current) return sharePreparationRef.current;
    const promise = (async () => {
      let imageDataUrl = coverDataUrlRef.current || asCoverDataUrl(resultPages[0]);
      if (!imageDataUrl) {
        imageDataUrl = canvasCoverFromImage(
          document.querySelector('.class-play-event__result .event-slide-viewer img'),
        );
        if (imageDataUrl) coverDataUrlRef.current = imageDataUrl;
      }
      if (!imageDataUrl) throw new Error('공유할 표지가 아직 없어요. 다시 만들어주세요.');
      const token = currentUser ? await currentUser.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(getApiUrl('/api/class-plays/share'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          image_data_url: imageDataUrl,
          class_label: normalizedClassLabel,
          game_id: selectedGame.id,
          game_label: selectedGame.label,
          visitor_id: getAnonymousUserId(),
          download_url: downloadUrlRef.current || '',
          page_urls: httpsAssetUrls(resultPagesRef.current),
        }),
      });
      if (!response.ok) throw await parseErrorDetail(response);
      const data = await response.json();
      shareRecordRef.current = data;
      setShareRecord(data);
      try {
        const saved = JSON.parse(sessionStorage.getItem(RESULT_STORAGE_KEY) || 'null') || {};
        sessionStorage.setItem(RESULT_STORAGE_KEY, JSON.stringify({
          ...saved,
          coverDataUrl: coverDataUrlRef.current || saved.coverDataUrl,
          shareRecord: data,
        }));
      } catch (_) {
        /* ignore */
      }
      return data;
    })();
    sharePreparationRef.current = promise;
    const clearPendingShare = () => {
      if (sharePreparationRef.current === promise) sharePreparationRef.current = null;
    };
    promise.then(clearPendingShare, clearPendingShare);
    return promise;
  };
  prepareShareRecordRef.current = prepareShareRecord;

  const attachShareAssets = async () => {
    if (sharePreparationRef.current) {
      try {
        await sharePreparationRef.current;
      } catch (_) {
        /* 표지 예약이 실패해도 아래에서 다시 만들 수 있다 */
      }
    }
    const shared = shareRecordRef.current;
    if (!shared?.share_id) return prepareShareRecord();
    const downloadUrl = downloadUrlRef.current || '';
    const pageUrls = httpsAssetUrls(resultPagesRef.current);
    if (!downloadUrl && pageUrls.length === 0) return shared;
    if (
      shared.download_url === downloadUrl
      && pageUrls.every((url) => (shared.pages || []).includes(url))
    ) {
      return shared;
    }
    const token = currentUser ? await currentUser.getIdToken() : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(
      getApiUrl(`/api/class-plays/share/${encodeURIComponent(shared.share_id)}/assets`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          visitor_id: getAnonymousUserId(),
          download_url: downloadUrlRef.current || '',
          page_urls: httpsAssetUrls(resultPagesRef.current),
        }),
      },
    );
    if (!response.ok) throw await parseErrorDetail(response);
    const data = await response.json();
    shareRecordRef.current = data;
    setShareRecord(data);
    try {
      const saved = JSON.parse(sessionStorage.getItem(RESULT_STORAGE_KEY) || 'null') || {};
      sessionStorage.setItem(RESULT_STORAGE_KEY, JSON.stringify({
        ...saved,
        downloadUrl: downloadUrlRef.current || saved.downloadUrl,
        shareRecord: data,
      }));
    } catch (_) {
      /* ignore */
    }
    return data;
  };
  attachShareAssetsRef.current = attachShareAssets;

  const handleKakaoShare = async () => {
    if (!resultPages.length || isSharing) return;
    setIsSharing(true);
    setError('');
    try {
      const shared = await attachShareAssets();
      const shareData = {
        title: `${normalizedClassLabel} ${selectedGame.label}`,
        text: '문제까지 채워진 교실 놀이 PPT.',
        url: shared.share_url,
        imageUrl: shared.image_url,
        buttonTitle: '나도 만들기',
      };
      let result = await shareToKakao(shareData);
      if (!result.success) result = await shareContent(shareData);
      if (!result.success && result.method !== 'cancelled') {
        setManualShareUrl(shared.share_url);
        throw new Error('카카오 공유를 열지 못했어요. 아래 링크 복사를 이용해주세요.');
      }
      if (result.success) {
        showToast(
          result.method === 'kakao'
            ? '카카오톡 공유 화면을 열었어요.'
            : '공유 링크를 복사했어요.',
          'success',
          5000,
        );
        trackClassPlayEvent('share_clicked', {
          entry: result.method || 'unknown',
          isAnonymous: !currentUser,
          isPro: false,
          shareId: shared.share_id,
          stage: 'visual_ready',
        });
      }
    } catch (shareError) {
      setError(toShareErrorMessage(shareError, Boolean(resultPages.length)));
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!resultPages.length || isSharing) return;
    setIsSharing(true);
    setError('');
    try {
      const shared = await attachShareAssets();
      const copied = await copyToClipboard(shared.share_url);
      setManualShareUrl(copied ? '' : shared.share_url);
      if (copied) {
        showToast('공유 링크를 복사했어요. 카카오톡에 붙여넣어 보내보세요.', 'success', 5000);
        trackClassPlayEvent('share_clicked', {
          entry: 'clipboard',
          isAnonymous: !currentUser,
          isPro,
          shareId: shared.share_id,
          stage: 'visual_ready',
        });
      } else {
        setError('자동 복사가 막혔어요. 아래 주소를 길게 눌러 복사해주세요.');
      }
    } catch (copyError) {
      setError(toShareErrorMessage(copyError, Boolean(resultPages.length)));
    } finally {
      setIsSharing(false);
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) {
      setError(
        isSharedResult
          ? '이 공유에는 아직 파일이 없어요. 만든 선생님이 다시 공유하면 받을 수 있어요.'
          : '저장할 파일이 아직 없어요.',
      );
      return;
    }
    downloadAndOpen(downloadUrl, `${normalizedClassLabel || '우리반'}-놀이.pptx`);
    trackClassPlayEvent('download_clicked', {
      entry: 'event_result',
      isAnonymous: !currentUser,
      isPro,
      stage: 'visual_ready',
    });
    showToast('놀이 PPT 파일을 저장했어요.', 'success', 4000);
  };

  const handleStartOwn = () => {
    trackClassPlayEvent('recipient_started', {
      entry: isSharedResult ? 'shared_result' : 'result_retry',
      isAnonymous: !currentUser,
      isPro: false,
      shareId: shareRecord?.share_id,
      stage: 'event_entry',
    });
    setResultPages([]);
    resultPagesRef.current = [];
    setDownloadUrl('');
    downloadUrlRef.current = '';
    setShareRecord(null);
    shareRecordRef.current = null;
    sharePreparationRef.current = null;
    setIsSharedResult(false);
    setManualShareUrl('');
    setShowAccountActions(false);
    coverDataUrlRef.current = '';
    setCoverReady(false);
    setError('');
    sessionStorage.removeItem(RESULT_STORAGE_KEY);
    window.history.replaceState({}, '', '/events/class-play');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const requireAccount = (intent = 'continue') => {
    if (currentUser) return true;
    persistDraft();
    setPendingFreeTrial({
      triggerFeature: `class_play_event_${intent}`,
      triggerPage: '/events/class-play',
      ttlMs: 30 * 60 * 1000,
    });
    navigate(`/auth?tab=signup&redirect=${encodeURIComponent(
      intent === 'save' ? '/library' : '/events/class-play',
    )}`);
    return false;
  };

  const openTrialGate = (intent, entry) => {
    trackClassPlayEvent('account_gate_viewed', {
      entry,
      isAnonymous: !currentUser,
      isPro: false,
      stage: 'account_gate',
      targetCategory: intent,
    });
    setTrialGateIntent(intent);
  };

  const handleTrialGateStart = async () => {
    const intent = trialGateIntent || 'continue';
    if (!currentUser) {
      trackClassPlayEvent('signup_clicked', {
        entry: 'trial_gate',
        isAnonymous: true,
        isPro: false,
        stage: 'account_gate',
        targetCategory: intent,
      });
      persistDraft();
      setTrialGateIntent('');
      requireAccount(intent);
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      const eventId = createStartTrialEventId();
      const response = await fetch(getApiUrl('/api/payment/subscription/free-trial'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildStartTrialMetaPayload({
          eventId,
          triggerFeature: `class_play_event_${intent}`,
          triggerPage: '/events/class-play',
        })),
      });
      const data = await response.json();
      if (!data?.success) {
        setError('7일 PRO 체험을 이미 사용했다면 PRO에서 계속 이용할 수 있어요.');
        setTrialGateIntent('');
        return;
      }
      trackFreeTrialStart(`class_play_event_${intent}`);
      trackClassPlayEvent('trial_started', {
        entry: 'trial_gate',
        isAnonymous: false,
        isPro: true,
        stage: 'trial_active',
        targetCategory: intent,
      });
      trackMetaStartTrialPixel({
        contentName: `class_play_event_${intent}`,
        eventId: data.meta_event_id || eventId,
      });
      await fetchSubscriptionStatus(true);
      showToast('축하해요! 7일 PRO 무료 체험이 시작됐어요.', 'success', 6000);
      setQuotaExhausted(false);
      setError('');
      setTrialGateIntent('');
      if (intent === 'save') navigate('/library');
    } catch (_) {
      setError('7일 PRO 체험을 시작하지 못했어요. 잠시 후 다시 시도해주세요.');
      setTrialGateIntent('');
    }
  };

  const handleToolAction = async (targetCategory) => {
    if (targetCategory === 'library') {
      trackClassPlayEvent('library_clicked', {
        entry: 'event_result',
        isAnonymous: !currentUser,
        isPro,
        stage: 'visual_ready',
        targetCategory: 'library',
      });
      navigate('/library');
      return;
    }
    if (targetCategory === 'save') {
      if (!currentUser) {
        openTrialGate('save', 'result_save');
        return;
      }
      trackClassPlayEvent('library_clicked', {
        entry: 'result_save',
        isAnonymous: false,
        isPro,
        stage: 'visual_ready',
        targetCategory: 'save',
      });
      navigate('/library');
      return;
    }
    setAccountActionBusy(targetCategory);
    setError('');
    try {
      const shared = await prepareShareRecord();
      const referenceImageUrl = shared.image_url;
      if (!referenceImageUrl) throw new Error('result_upload_failed');
      const sourceContent = [
        `[우리 반] ${normalizedClassLabel}`,
        `[놀이] ${selectedGame.label}`,
        phrase ? `[응원 문구] ${normalizeClassPlayPhrase(phrase)}` : '',
        classStory ? `[우리 반 이야기] ${normalizeClassPlayStory(classStory)}` : '',
        '',
        '[자료 요청]',
        targetCategory === 'worksheet_gen'
          ? '이 놀이 PPT를 인쇄해서 바로 쓰는 활동지로 만들어주세요.'
          : '이 놀이 PPT를 이어서 수정하고 TV에 바로 틀 수 있게 다듬어주세요.',
        '학생 이름·학년 칸은 넣지 마세요.',
      ].filter(Boolean).join('\n');
      const payload = createChatHandoffPayload({
        href: `/chat?category=${targetCategory}`,
        referenceImageUrl,
        referenceMode: 'first_page',
        referenceTitle: `${normalizedClassLabel} ${selectedGame.label}`,
        referenceTypeLabel: '우리 반 놀이 PPT',
        sourceCategory: 'ppt_maker',
        sourceContent,
        sourcePurpose: 'classPlay',
      });
      payload.eventEntry = 'class_play_event';
      payload.eventResultUrl = referenceImageUrl;
      trackClassPlayEvent('handoff_clicked', {
        entry: 'event_result',
        isAnonymous: !currentUser,
        isPro,
        stage: 'visual_ready',
        targetCategory,
      });
      const handoffId = storeChatHandoff(payload);
      if (handoffId) {
        navigate(buildChatHandoffUrl(targetCategory, handoffId));
      } else {
        navigate('/chat', { state: { chatHandoff: payload } });
      }
    } catch (_) {
      setError('왓퀴즈 수업자료로 이어가지 못했어요. 다시 시도해주세요.');
    } finally {
      setAccountActionBusy('');
    }
  };

  const handleCrossHandoff = () => {
    sessionStorage.setItem(ACTIVITY_DRAFT_KEY, JSON.stringify({
      classLabel: normalizedClassLabel,
      phrase: normalizeClassPlayPhrase(phrase),
      classStory: normalizeClassPlayStory(classStory),
      from: 'class_play',
    }));
    trackClassPlayEvent('cross_handoff_clicked', {
      entry: 'event_result',
      isAnonymous: !currentUser,
      isPro: false,
      stage: 'visual_ready',
      targetCategory: 'class_activity',
    });
    navigate('/events/class-activity?from=class-play');
  };

  const handleHwpCrossLink = () => {
    trackClassPlayEvent('cross_handoff_clicked', {
      entry: 'event_result',
      isAnonymous: !currentUser,
      isPro: false,
      stage: 'visual_ready',
      targetCategory: 'hwp_doc',
    });
    navigate('/events/hwp-exam?from=class-play');
  };

  return (
    <main className="class-photo-event class-play-event">
      <Helmet>
        <title>우리 반 교실 놀이 PPT | 왓퀴즈</title>
        <meta
          name="description"
          content="밸런스게임·초성퀴즈·골든벨 PPT를 문제까지 채워서 3분 만에 만들어요. TV에 틀면 바로 시작."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="class-photo-event__header">
        <a href="/" aria-label="왓퀴즈 홈">whatquiz</a>
        <span>우리 반 교실 놀이</span>
      </header>

      {!resultPages.length && (
        <section className="class-photo-event__hero">
          <h1>문제까지 채워진<br />우리 반 놀이 PPT</h1>
          <p>템플릿에 문제 채우던 시간, 이제 3분이면 돼요. TV에 틀면 바로 시작.</p>
        </section>
      )}

      {!resultPages.length && (
        <section
          className="class-photo-event__example class-play-event__example"
          aria-label="교실 놀이 미리보기"
        >
          <div className="class-photo-event__example-slides">
            {CLASS_PLAY_EXAMPLES.map((example, index) => (
              <img
                key={example.id}
                src={example.exampleImage}
                alt={example.exampleAlt}
                loading={index === 0 ? 'eager' : 'lazy'}
                className={index === exampleSlide ? 'active' : ''}
              />
            ))}
          </div>
        </section>
      )}

      {!resultPages.length && (
        <section className="class-photo-event__maker" aria-label="우리 반 놀이 PPT 만들기">
          {notice && <p className="class-photo-event__notice">{notice}</p>}
          {fromActivity && (
            <p className="class-photo-event__notice">활동지에서 이어서 만들고 있어요.</p>
          )}

          <div className="class-photo-event__styles">
            <span className="class-photo-event__styles-label">어떤 놀이로 할까요?</span>
            <div className="class-play-event__game-grid" role="radiogroup" aria-label="게임 유형">
              {CLASS_PLAY_GAME_TYPES.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  role="radio"
                  aria-checked={gameTypeId === game.id}
                  className={`class-play-event__game ${gameTypeId === game.id ? 'selected' : ''}`}
                  onClick={() => {
                    setGameTypeId(game.id);
                    const exampleIndex = CLASS_PLAY_EXAMPLES.findIndex((item) => item.id === game.id);
                    if (exampleIndex >= 0) setExampleSlide(exampleIndex);
                  }}
                >
                  <strong>{game.label}</strong>
                  {game.default && <em>기본</em>}
                </button>
              ))}
            </div>
          </div>

          <div className="class-play-event__chips" role="radiogroup" aria-label="주제">
            {CLASS_PLAY_TOPICS.map((topic) => (
              <button
                key={topic.id}
                type="button"
                role="radio"
                aria-checked={topicId === topic.id}
                className={topicId === topic.id ? 'active' : ''}
                onClick={() => setTopicId(topic.id)}
              >
                {topic.label}
              </button>
            ))}
          </div>
          {topicId === 'free' && (
            <label className="class-play-event__field">
              <span>직접 입력 주제</span>
              <input
                maxLength={CLASS_PLAY_MAX_FREE_TOPIC_CHARS}
                placeholder="급식 메뉴"
                value={freeTopic}
                onChange={(event) => setFreeTopic(event.target.value)}
              />
            </label>
          )}

          {CLASS_PLAY_QUESTION_COUNTS.length > 1 && (
            <div className="class-play-event__chips" role="radiogroup" aria-label="문제 수">
              {CLASS_PLAY_QUESTION_COUNTS.map((option) => (
                <button
                  key={option.count}
                  type="button"
                  role="radio"
                  aria-checked={questionCount === option.count}
                  className={questionCount === option.count ? 'active' : ''}
                  onClick={() => setQuestionCount(option.count)}
                >
                  문제 {option.count}개 · {option.slides}장
                  {option.default ? ' · 기본' : ''}
                </button>
              ))}
            </div>
          )}

          <div className="class-play-event__chips" role="radiogroup" aria-label="반 이름">
            {CLASS_PLAY_CLASS_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                role="radio"
                aria-checked={!customClass && classLabel === preset}
                className={!customClass && classLabel === preset ? 'active' : ''}
                onClick={() => {
                  setCustomClass(false);
                  setClassLabel(preset);
                }}
              >
                {preset}
              </button>
            ))}
            <button
              type="button"
              role="radio"
              aria-checked={customClass}
              className={customClass ? 'active' : ''}
              onClick={() => {
                setCustomClass(true);
                if (CLASS_PLAY_CLASS_PRESETS.includes(classLabel)) {
                  setClassLabel('');
                }
              }}
            >
              직접 쓰기
            </button>
          </div>
          {customClass && (
            <label className="class-play-event__field">
              <span>반 이름</span>
              <input
                maxLength={CLASS_PLAY_MAX_CLASS_LABEL_CHARS}
                placeholder="3학년 2반"
                value={classLabel}
                onChange={(event) => setClassLabel(event.target.value)}
              />
            </label>
          )}

          <details className="class-play-event__more">
            <summary>응원 문구·우리 반 이야기 넣기</summary>
            <label className="class-play-event__field">
              <span>응원 문구</span>
              <input
                maxLength={CLASS_PLAY_MAX_PHRASE_CHARS}
                placeholder="우리 반 파이팅!"
                value={phrase}
                onChange={(event) => setPhrase(event.target.value)}
              />
            </label>
            <label className="class-play-event__field">
              <span>우리 반 이야기</span>
              <input
                maxLength={CLASS_PLAY_MAX_STORY_CHARS}
                placeholder="요즘 우리 반 유행어나 재밌었던 일 (예: 급식 먹고 축구가 국룰)"
                value={classStory}
                onChange={(event) => setClassStory(event.target.value)}
              />
            </label>
          </details>

          <button
            type="button"
            className="class-photo-event__generate"
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            우리 반 놀이 PPT 만들기
          </button>
          {quotaExhausted && (
            <button
              type="button"
              className="class-photo-event__quota-login"
              onClick={() => openTrialGate('continue', 'quota')}
            >
              가입하면 7일 PRO로 계속 만들 수 있어요
            </button>
          )}
          {error && <p className="class-photo-event__error" role="alert">{error}</p>}

          {isGenerating && (
            <div className="class-photo-event__ai-loading class-play-event__loading" role="status" aria-live="polite">
              <div className="class-photo-event__ai-orbit" aria-hidden="true">
                <span className="orbit orbit-one" />
                <span className="orbit orbit-two" />
                <span className="spark spark-one">✦</span>
                <span className="spark spark-two">✦</span>
                <span className="spark spark-three">✦</span>
                <div className="class-photo-event__ai-core">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2.5 14.2 9l6.3 2.2-6.3 2.2L12 20l-2.2-6.6-6.3-2.2L9.8 9 12 2.5Z" fill="currentColor" />
                  </svg>
                </div>
              </div>
              <strong>{loadingMessage}</strong>
              <span>
                {previewDone
                  ? `${previewDone}/${previews.length || selectedCount.slides}장 들어왔어요. 나머지도 곧 나와요.`
                  : `표지·문제·엔딩 ${selectedCount.slides}장이 하나씩 들어와요. 3~5분이에요.`}
              </span>
              <div className="class-photo-event__ai-steps" aria-hidden="true">
                {LOADING_STEPS.map((label, index) => (
                  <i key={label} className={index <= loadingStep ? 'active' : ''}>
                    {label}
                  </i>
                ))}
              </div>
              <EventSlideViewer
                className="class-play-event__loading-viewer"
                slides={previews}
                expectedCount={selectedCount.slides}
                altPrefix="슬라이드 미리보기"
                followLatest
                onOpen={(src, index) => setPreviewLightbox({
                  src,
                  alt: `${index + 1}슬라이드 미리보기`,
                })}
              />
            </div>
          )}
        </section>
      )}

      {Boolean(resultPages.length) && (
        <section className="class-photo-event__result class-play-event__result" aria-label="완성된 놀이 PPT">
          <span>{normalizedClassLabel || '우리 반'} 놀이 PPT가 완성됐어요</span>
          <EventSlideViewer
            className="class-play-event__viewer"
            slides={resultPages}
            altPrefix="슬라이드"
          />
          {error && <p className="class-photo-event__error" role="alert">{error}</p>}
          <div className="class-photo-event__result-actions">
            <button type="button" className="primary" onClick={handleKakaoShare} disabled={isSharing}>
              카카오톡으로 자랑하기
            </button>
            <button type="button" onClick={handleDownload}>PPT 파일 저장</button>
          </div>
          <button type="button" className="class-photo-event__copy-link" onClick={handleCopyShareLink}>
            링크 복사
          </button>
          {isSharedResult && (
            <button type="button" className="class-photo-event__copy-link" onClick={handleStartOwn}>
              나도 만들기
            </button>
          )}
          {manualShareUrl && (
            <input className="class-photo-event__manual-link" readOnly value={manualShareUrl} />
          )}
          {!isSharedResult && (
            <div className="class-photo-event__account">
              {!showAccountActions && !isPro ? (
                <button type="button" onClick={() => setShowAccountActions(true)}>
                  <strong>이 PPT로 더 만들어보기</strong>
                  <span>수정 · 학습지 · 저장 · 라이브러리</span>
                </button>
              ) : (
                <>
                  {isPro && (
                    <div className="class-photo-event__pro-guide">
                      <strong>PRO로 다음 자료도 바로 만들어보세요</strong>
                      <span>PPT와 학습지를 이어서 수정하고 라이브러리에 저장할 수 있어요.</span>
                    </div>
                  )}
                  <div className="class-photo-event__account-actions">
                    <button
                      type="button"
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('ppt_maker')}
                    >
                      {accountActionBusy === 'ppt_maker' ? '연결 중...' : '수정하러 가기'}
                    </button>
                    <button
                      type="button"
                      className={isPro ? 'recommended' : ''}
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('worksheet_gen')}
                    >
                      {accountActionBusy === 'worksheet_gen' ? '연결 중...' : '학습지 만들기'}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('save')}
                    >
                      내 라이브러리에 저장
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('library')}
                    >
                      라이브러리 구경
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            className="class-play-event__cross"
            onClick={handleCrossHandoff}
          >
            이 주제로 활동지도 만들기
          </button>
          <button
            type="button"
            className="class-play-event__cross"
            onClick={handleHwpCrossLink}
          >
            시험지·가정통신문도 한글 파일로 →
          </button>
        </section>
      )}

      {isSharing && !shareRecord && (
        <div className="class-photo-event__share-loading">
          <div>
            <span aria-hidden="true">{StrokeIcons.chat}</span>
            <strong>공유 카드를 만들고 있어요</strong>
            <small>잠시만 기다려주세요</small>
          </div>
        </div>
      )}

      {previewLightbox && (
        <div
          className="class-play-event__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="미리보기 확대"
          onClick={() => setPreviewLightbox(null)}
        >
          <img
            src={previewLightbox.src}
            alt={previewLightbox.alt}
            onClick={(event) => event.stopPropagation()}
          />
          <button type="button" onClick={() => setPreviewLightbox(null)}>
            닫기
          </button>
        </div>
      )}

      <ClassPhotoTrialGate
        isOpen={Boolean(trialGateIntent)}
        intentLabel={trialGateIntent === 'save' ? '라이브러리에 저장' : '계속 만들기'}
        onClose={() => setTrialGateIntent('')}
        onStart={handleTrialGateStart}
      />
    </main>
  );
};

export default ClassPlayEventPage;
